import React, { FunctionComponent, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useConfirm } from 'material-ui-confirm';

import {
  adminCreateInvoiceAdminNote,
  adminDeleteInvoiceAdminNote,
  IInvoice,
  IInvoiceAdminNote,
} from '../../../../../../store/V2/InvoicesSlice';
import { TPTextField } from '../../../../../../components/Inputs';
import DarkLoadingButton, {
  defaultButtonStyles,
} from '../../../../../../components/V2/Buttons';
import { IUser } from '../../../../../../store/UserSlice';
import AdminNoteCard from '../../../../../../components/V2/Cards/AdminNoteCard';

interface AdminNotesModalProps {
  open: boolean;
  onClose: () => void;
  invoice: IInvoice;
}

const AdminNotesModal: FunctionComponent<AdminNotesModalProps> = ({
  open,
  onClose,
  invoice,
}) => {
  const dispatch = useDispatch();
  const confirm = useConfirm();

  // State
  const [adminNoteText, setAdminNoteText] = useState('');
  const [isLoading, setLoading] = useState(false);
  const [isDeleteLoading, setDeleteLoading] = useState(false);

  const adminNotes = invoice.adminNotes || [];

  const handleSubmit = async () => {
    setLoading(true);
    setAdminNoteText('');

    await dispatch(
      adminCreateInvoiceAdminNote({
        id: invoice._id,
        description: adminNoteText,
      })
    );

    setLoading(false);
  };

  const handleDeleteNote = async (adminNote: IInvoiceAdminNote) => {
    confirm({
      title: 'Are you sure?',
      description: 'Do you want to delete this note?',
    }).then(async () => {
      setDeleteLoading(true);

      await dispatch(
        adminDeleteInvoiceAdminNote({
          id: invoice._id,
          noteId: adminNote._id,
        })
      );

      setDeleteLoading(false);
    });
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { borderRadius: '20px' },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: { xs: '15px', sm: '30px' },
          color: '#000000',
          fontWeight: 600,
          fontSize: '16px',
        }}
      >
        Admin Notes
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: { xs: '7px', sm: '22px' },
          top: { xs: '7px', sm: '22px' },
        }}
      >
        <CloseIcon sx={{ color: '#000000' }} />
      </IconButton>
      <DialogContent
        sx={{ p: { xs: '0 15px 15px 15px', sm: '0 30px 30px 30px' } }}
      >
        <div style={{ paddingTop: '6px' }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <TPTextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Admin Note"
                  placeholder="Enter here..."
                  value={adminNoteText}
                  InputLabelProps={{ shrink: true }}
                  onChange={(event) => setAdminNoteText(event.target.value)}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <DarkLoadingButton
                disableElevation
                variant="contained"
                color="inherit"
                onClick={handleSubmit}
                loading={isLoading}
                disabled={!adminNoteText.trim().length}
                sx={{ ...defaultButtonStyles }}
              >
                Add Note
              </DarkLoadingButton>
            </Grid>
            <Grid item xs={12}>
              <Typography fontSize="14px" fontWeight={600}>
                Notes
              </Typography>
            </Grid>
            {adminNotes.map((adminNote) => {
              return (
                <Grid item xs={12} key={adminNote._id}>
                  <AdminNoteCard
                    disableDelete={isDeleteLoading}
                    onDelete={() => handleDeleteNote(adminNote)}
                    note={{
                      _id: adminNote._id,
                      createdAt: adminNote.createdAt,
                      description: adminNote.description,
                      createdBy: adminNote.createdBy as IUser,
                    }}
                  />
                </Grid>
              );
            })}
            {!adminNotes.length ? (
              <Grid item xs={12}>
                <Typography fontSize="14px">
                  No admin notes are available
                </Typography>
              </Grid>
            ) : null}
          </Grid>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AdminNotesModal;
